/**
 * Isometric projection, colour maths and the token bridge, shared by the map painters and
 * any preview that needs them. Ported from the design handoff in
 * `misc/design_handoff_hedgerow` under D148; every constant is the handoff's own.
 *
 * Pure but for one thing: `resolveMapTokens` touches the DOM, because the only way to read
 * a resolved custom property is to put it on a probe element. Nothing else in this file or
 * in `iso-paint.ts` reads the document, the clock or anything else outside its arguments.
 *
 * D149 is the one place the handoff is wrong about this codebase: its parcel is a
 * rectangle, and a field here is any connected set of tiles. So a parcel carries a set of
 * tile keys and the outline of their union is computed in world space and projected, which
 * is what keeps a seam from showing down the middle of an L-shaped field.
 */
import type { Weather } from '@hedgerow/sim';
import type { Maker } from './makers.ts';
import {
  CROP_HUE,
  PATH_DECOR,
  RICHER,
  SNOW,
  ROOT_LEAF,
  type AnimalKind as LookAnimal,
} from '../idle/appearance.ts';

/** D284: the crop looks live in the shared appearance module; kept reachable from here. */
export { ROOT_LEAF };

export const TW = 46,
  TH = 23,
  SLAB = 16;

/** A point in screen space, after projection. */
export type Point = [number, number];

/* ---------- which way the world is turned (D176) ----------
   The farm turns in four ninety degree steps, and `project` and `unproject` are the one
   gate every coordinate on the map passes through. So the turn lives here rather than in
   any painter: a quarter turn of world space before the projection, and the same turn
   undone after the inverse. Everything built on tile coordinates — the union outlines, the
   boundary runs, the row spans, a machine's run, a scatter — is carried by that alone.

   It is module state rather than an argument because the painters are a hundred call sites
   deep in `project` and threading a turn through every one of them would buy nothing. The
   contract is `withTurn`: it is set for the length of one synchronous paint and put back
   afterwards, so a still painted in the same frame is still painted at rest (D168), and
   nothing outside a paint ever sees anything but the default.

   The turn is about the plot's own centre, so the farm stays where it was on screen rather
   than swinging away from the eye and having to be fitted again.

   The light does not turn with it. D176: the hour's direction stays fixed to the screen, so
   what each painter needs is not the world facing of an edge but the facing it presents to
   the viewer, which is `screenFacing`. */

/** How many quarter turns the world is round from its default. */
export type Turn = 0 | 1 | 2 | 3;

/** The number of orientations. Four, and it is not a free angle (D176). */
export const TURNS = 4;

interface TurnState {
  t: Turn;
  cx: number;
  cy: number;
}

let TURN: TurnState = { t: 0, cx: 0, cy: 0 };

/** The turn in force, and the world point it is about. */
export const mapTurn = (): { t: Turn; cx: number; cy: number } => TURN;

/** The next orientation round. Four steps return to where it started (D176). */
export const stepTurn = (t: Turn): Turn => ((t + 1) % TURNS) as Turn;

/**
 * Set the turn. The pivot is held to the half-tile lattice, which keeps a tile corner a
 * tile corner at every orientation and keeps the round trip through `unproject` exact.
 */
export function setMapTurn(t: number, cx = 0, cy = 0): void {
  TURN = {
    t: (((Math.trunc(t) % TURNS) + TURNS) % TURNS) as Turn,
    cx: Math.round(cx * 2) / 2,
    cy: Math.round(cy * 2) / 2,
  };
}

/**
 * Run one synchronous paint at a turn, then put back whatever was in force. Synchronous
 * only: nothing inside may await, or a second map would paint at the first one's turn.
 */
export function withTurn<T>(t: number, cx: number, cy: number, fn: () => T): T {
  const prev = TURN;
  setMapTurn(t, cx, cy);
  try {
    return fn();
  } finally {
    TURN = prev;
  }
}

/** The world point as the turn leaves it, before the projection proper. */
export function turnWorld(x: number, y: number): Point {
  const { t, cx, cy } = TURN;
  if (!t) return [x, y];
  const dx = x - cx,
    dy = y - cy;
  if (t === 1) return [cx + dy, cy - dx];
  if (t === 2) return [cx - dx, cy - dy];
  return [cx - dy, cy + dx];
}

/** The inverse of `turnWorld`, exactly. A tap is only as good as this is. */
export function unturnWorld(x: number, y: number): Point {
  const { t, cx, cy } = TURN;
  if (!t) return [x, y];
  const dx = x - cx,
    dy = y - cy;
  if (t === 1) return [cx - dy, cy + dx];
  if (t === 2) return [cx - dx, cy - dy];
  return [cx + dy, cy - dx];
}

/**
 * How near the eye a world point is, at this turn. The one sort key for everything drawn
 * back to front; `x + y` is what it comes to at rest, and it is wrong at every other turn.
 */
export const depth = (x: number, y: number): number => {
  const [tx, ty] = turnWorld(x, y);
  return tx + ty;
};

/** Whether a world direction runs toward the eye. Both axes do at rest. */
export const towardEye = (dx: number, dy: number): boolean => depth(dx, dy) > depth(0, 0);

/**
 * The same world point, a hair further from the eye whatever the turn. Two things that
 * meet at a corner sort by this rather than by a nudge along an axis that may now run the
 * other way.
 */
export const stepBack = (x: number, y: number, d = 0.01): Point => {
  const [tx, ty] = turnWorld(x, y);
  return unturnWorld(tx, ty - d);
};

/* The facings in the order a quarter turn walks them: north presents as west, west as
   south, and so round. */
const FACINGS = ['n', 'w', 's', 'e'] as const;

/**
 * The facing a world facing presents to the viewer at this turn. The light is fixed to the
 * screen (D176), so this is what decides whether an edge or a wall is lit, and which sides
 * of a shape are the far ones.
 */
export function screenFacing(f: Facing): Facing {
  const i = FACINGS.indexOf(f);
  return i < 0 ? f : (FACINGS[(i + TURN.t) % TURNS] as Facing);
}

/**
 * Which corner of a world rectangle is nearest the eye: 0 is (x, y), 1 (x + w, y),
 * 2 (x + w, y + h), 3 (x, y + h). At rest it is always 2, which is the pair of walls an
 * isometric box has always shown; at another turn it is one of the other three, and the
 * two walls that meet there are the two the viewer can see.
 */
export function frontCorner(x: number, y: number, w: number, h: number): 0 | 1 | 2 | 3 {
  const pts: Point[] = [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
  let best = 0,
    bd = -Infinity;
  pts.forEach((p, i) => {
    const d = depth(p[0], p[1]);
    if (d > bd) {
      bd = d;
      best = i;
    }
  });
  return best as 0 | 1 | 2 | 3;
}

/* ---------- which projection the world is drawn in (D178) ----------
   The farm can also be seen from directly above. That is a second projection at the same
   gate, not a second map: `project` and `unproject` are still the one place a coordinate
   passes through, so the plan view inherits the turn, the tap, Fit and the overlays
   without any of them being written twice. It is the same farm in the same art (D168);
   the flat grid deleted in 0.7.35 is not coming back.

   A plan has no eye and nothing has height, so `z` is dropped here rather than squashed.
   Everything that was drawn as a side — a wall, a gable, a bank's shoulder, the slab's
   skirt — is left out by the painter instead, because a squashed isometric drawing reads
   as a mistake rather than as a choice.

   Module state, set for one synchronous paint, exactly as the turn is. A still is a
   picture rather than a tool for reading the farm, and stays isometric (D178), which
   `withPlan(false, …)` round its own paint is what guarantees. */

/** Which projection the map is drawn in. */
export type Projection = 'iso' | 'plan';

/**
 * How many pixels a tile takes each way from above. Between the isometric tile's 46 across
 * and 23 down, so a world covers about the ground it did and every painter's pixel sizes —
 * a plant, a rock, a machine — still read at the same zoom.
 */
export const PLAN_TILE = 32;

let VIEW: Projection = 'iso';

/** The projection in force. */
export const mapView = (): Projection => VIEW;

/** Whether the world is being drawn from above. The question every painter asks. */
export const isPlan = (): boolean => VIEW === 'plan';

/** Set the projection. Paired with `withPlan`, which is how a paint should reach it. */
export function setMapView(v: Projection): void {
  VIEW = v;
}

/**
 * Run one synchronous paint from above, or from the side, then put back whatever was in
 * force. Synchronous only, for the reason `withTurn` is.
 */
export function withPlan<T>(on: boolean, fn: () => T): T {
  const prev = VIEW;
  VIEW = on ? 'plan' : 'iso';
  try {
    return fn();
  } finally {
    VIEW = prev;
  }
}

export const project = (x: number, y: number, z = 0): Point => {
  const [wx, wy] = turnWorld(x, y);
  if (VIEW === 'plan') return [wx * PLAN_TILE, wy * PLAN_TILE];
  return [((wx - wy) * TW) / 2, ((wx + wy) * TH) / 2 - z];
};
export const unproject = (sx: number, sy: number): Point => {
  if (VIEW === 'plan') return unturnWorld(sx / PLAN_TILE, sy / PLAN_TILE);
  const a = sx / (TW / 2),
    b = sy / (TH / 2);
  return unturnWorld((b + a) / 2, (b - a) / 2);
};
/**
 * D233: which way the world's north points on screen, in degrees clockwise from straight up.
 * North is the sim's smaller y, the way the strip names read it, so this is the grid step
 * (0, -1) put through `project` at the given turn and view: the compass can never disagree
 * with the map, because it asks the same gate the map is drawn through.
 */
export function northOnScreen(t: number, plan: boolean): number {
  const [a, b] = withPlan(plan, () => withTurn(t, 0, 0, () => [project(0, 0), project(0, -1)]));
  const deg = (Math.atan2(b[0] - a[0], a[1] - b[1]) * 180) / Math.PI;
  return (deg + 360) % 360;
}
export const polyPath = (pts: Point[]): string =>
  'M' + pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L') + ' Z';
export const rectPts = (x: number, y: number, w: number, h: number, z = 0): Point[] => [
  project(x, y, z),
  project(x + w, y, z),
  project(x + w, y + h, z),
  project(x, y + h, z),
];

const hex2rgb = (h: string): number[] => {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgb2hex = (c: number[]): string =>
  '#' +
  c
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
export const mix = (a: string, b: string, t: number): string => {
  const A = hex2rgb(a),
    B = hex2rgb(b);
  return rgb2hex(A.map((v, i) => v + ((B[i] as number) - v) * t));
};
/** D381: a colour made richer by `t` (0.1 is a tenth more), pushed away from its own grey. */
export const vivid = (c: string, t: number): string => {
  if (!/^#[0-9a-f]{6}$/i.test(c)) return c;
  const C = hex2rgb(c);
  const grey = 0.299 * (C[0] as number) + 0.587 * (C[1] as number) + 0.114 * (C[2] as number);
  return rgb2hex(C.map((v) => grey + (v - grey) * (1 + t)));
};
export const shade = (c: string, t: number): string => mix(c, '#000000', t);
export const tint = (c: string, t: number): string => mix(c, '#ffffff', t);
export const alpha = (c: string, a: number): string => {
  const [r, g, b] = hex2rgb(c);
  return `rgba(${r},${g},${b},${a})`;
};
/**
 * The map's only randomness, and it is not random: a seed taken from the parcel's own
 * position gives the same tufts and the same rocks to every player looking at that field,
 * which is what rule 7 asks for.
 */
export const rnd = (seed: number): (() => number) => {
  let s = seed * 9301 + 49297;
  return () => (s = (s * 9301 + 49297) % 233280) / 233280;
};

/* ---------- a field of any shape ---------- */

/** A tile key, `"x,y"`, as everywhere else in the codebase. */
export type TileKey = string;

/** Which way a boundary edge faces, which is what decides whether it catches the light. */
export type Facing = 'n' | 'e' | 's' | 'w';

/** One run of a union's boundary, in world space. */
export interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  facing: Facing;
}

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** D344: a world's own edge, with the ground inside it that is cut from its outline. */
export interface WorldBounds extends Bounds {
  cuts?: Bounds[];
}

/**
 * D344: what both renderers draw for a world's edge: the slab under it, as rectangles from the
 * back, and its hedge along the far sides of its outline (north and west, as D301 drew a plain
 * world's two far sides), as runs from one tile corner to another. A world with no cuts gives its
 * own rectangle and its two far sides, exactly as before.
 */
export function worldOutline(fp: WorldBounds): {
  slabs: Bounds[];
  hedges: Array<[number, number, number, number]>;
} {
  if (!fp.cuts?.length)
    return {
      slabs: [fp],
      hedges: [
        [fp.x, fp.y, fp.x + fp.w, fp.y],
        [fp.x, fp.y, fp.x, fp.y + fp.h],
      ],
    };
  const cut = new Set<string>();
  for (const c of fp.cuts)
    for (let y = c.y; y < c.y + c.h; y++)
      for (let x = c.x; x < c.x + c.w; x++) cut.add(`${x},${y}`);
  const on = (x: number, y: number) =>
    x >= fp.x && x < fp.x + fp.w && y >= fp.y && y < fp.y + fp.h && !cut.has(`${x},${y}`);
  // The slab: each row's runs, a run carried down while the rows below repeat it.
  const slabs: Bounds[] = [];
  let open: Bounds[] = [];
  for (let y = fp.y; y <= fp.y + fp.h; y++) {
    const runs: Array<[number, number]> = [];
    for (let x = fp.x; x < fp.x + fp.w; x++)
      if (on(x, y)) {
        const last = runs[runs.length - 1];
        if (last && last[1] === x) last[1] = x + 1;
        else runs.push([x, x + 1]);
      }
    const next: Bounds[] = [];
    for (const r of open) {
      const same = runs.findIndex(([a, b]) => a === r.x && b === r.x + r.w);
      if (same >= 0) {
        r.h++;
        next.push(r);
        runs.splice(same, 1);
      } else slabs.push(r);
    }
    for (const [a, b] of runs) next.push({ x: a, y, w: b - a, h: 1 });
    open = next;
  }
  slabs.sort((a, b) => a.y - b.y || a.x - b.x);
  // The hedge: every tile edge with the world on its south or east side and none beyond.
  const hedges: Array<[number, number, number, number]> = [];
  for (let y = fp.y; y < fp.y + fp.h; y++)
    for (let x = fp.x; x < fp.x + fp.w; x++) {
      if (!on(x, y) || on(x, y - 1)) continue;
      const last = hedges[hedges.length - 1];
      if (last && last[1] === y && last[3] === y && last[2] === x) last[2] = x + 1;
      else hedges.push([x, y, x + 1, y]);
    }
  for (let x = fp.x; x < fp.x + fp.w; x++)
    for (let y = fp.y; y < fp.y + fp.h; y++) {
      if (!on(x, y) || on(x - 1, y)) continue;
      const last = hedges[hedges.length - 1];
      if (last && last[0] === x && last[2] === x && last[3] === y) last[3] = y + 1;
      else hedges.push([x, y, x, y + 1]);
    }
  return { slabs, hedges };
}

const tileSet = (tiles: Iterable<TileKey>): Set<TileKey> =>
  tiles instanceof Set ? (tiles as Set<TileKey>) : new Set(tiles);

/** `"3,4"` to `[3, 4]`, or null for anything that is not a tile key. */
export function parseTileKey(key: TileKey): [number, number] | null {
  const [x, y] = key.split(',').map(Number);
  if (x === undefined || y === undefined || Number.isNaN(x) || Number.isNaN(y)) return null;
  return [x, y];
}

/**
 * The bounding box of a tile set, in tiles. A field is rarely this shape; the texture
 * passes still step across it, and are clipped to the union as they go.
 */
export function unionBounds(tiles: Iterable<TileKey>): Bounds {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const key of tileSet(tiles)) {
    const p = parseTileKey(key);
    if (!p) continue;
    x0 = Math.min(x0, p[0]);
    y0 = Math.min(y0, p[1]);
    x1 = Math.max(x1, p[0] + 1);
    y1 = Math.max(y1, p[1] + 1);
  }
  if (!Number.isFinite(x0)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Tiles, so a scatter keeps its density on a shape that is not a rectangle. */
export function unionArea(tiles: Iterable<TileKey>): number {
  return tileSet(tiles).size;
}

/** Is a world point inside the union? Keeps a plant or a tuft on its own field. */
export function inUnion(tiles: Iterable<TileKey>, x: number, y: number): boolean {
  return tileSet(tiles).has(`${Math.floor(x)},${Math.floor(y)}`);
}

/**
 * The boundary of a tile set: one edge per tile side with nothing on the other side of it.
 * This is the marching-edges walk `reachEdgePath` in TileGrid does for the reach boundary
 * (D116) and the hedgerow (D133), with two things added. The edges come back directed and
 * wound clockwise, so they can be chained into a ring rather than left as loose segments,
 * and each one says which way it faces, so the painter knows whether it is lit or in shade.
 *
 * `clipY` cuts the set at a world y, which is how the worked band of a running job grows
 * from the near edge without the field having to be a rectangle.
 */
export function unionEdges(tiles: Iterable<TileKey>, clipY = Infinity): Edge[] {
  const set = tileSet(tiles);
  const out: Edge[] = [];
  for (const key of set) {
    const p = parseTileKey(key);
    if (!p) continue;
    const [x, y] = p;
    if (y >= clipY) continue;
    const yTop = y,
      yBot = Math.min(y + 1, clipY);
    if (yBot <= yTop) continue;
    if (!set.has(`${x},${y - 1}`)) out.push({ x1: x, y1: yTop, x2: x + 1, y2: yTop, facing: 'n' });
    if (!set.has(`${x + 1},${y}`))
      out.push({ x1: x + 1, y1: yTop, x2: x + 1, y2: yBot, facing: 'e' });
    if (!set.has(`${x},${y + 1}`) || y + 1 > clipY)
      out.push({ x1: x + 1, y1: yBot, x2: x, y2: yBot, facing: 's' });
    if (!set.has(`${x - 1},${y}`)) out.push({ x1: x, y1: yBot, x2: x, y2: yTop, facing: 'w' });
  }
  return out;
}

/**
 * Runs of edges that face the same way and lie end to end, joined into one segment each.
 * Shared by the union outline and the boundary between two parcels, so both come back as
 * whole sides rather than as one segment per tile.
 */
function mergeRuns(edges: Edge[]): Edge[] {
  const heads = new Set<string>();
  for (const e of edges) heads.add(`${e.facing}|${e.x2},${e.y2}`);
  const merged: Edge[] = [];
  const used = new Set<Edge>();
  for (const e of edges) {
    // Start only from an edge nothing else runs into: that is the head of a run.
    if (used.has(e) || heads.has(`${e.facing}|${e.x1},${e.y1}`)) continue;
    let tail = e;
    used.add(e);
    for (;;) {
      const next = edges.find(
        (n) => !used.has(n) && n.facing === tail.facing && n.x1 === tail.x2 && n.y1 === tail.y2,
      );
      if (!next) break;
      used.add(next);
      tail = next;
    }
    merged.push({ x1: e.x1, y1: e.y1, x2: tail.x2, y2: tail.y2, facing: e.facing });
  }
  return merged;
}

/**
 * Runs of boundary edges that face the same way and lie on the same line, joined into one
 * segment each. A rectangle comes back as its four sides, an L as its six.
 */
export function unionStrokes(tiles: Iterable<TileKey>, clipY = Infinity): Edge[] {
  return mergeRuns(unionEdges(tiles, clipY));
}

/**
 * The boundaries between parcels: one segment for every run of tile sides where the tile
 * on each side belongs to a different parcel (D161). An edge is emitted once, not once
 * per neighbour, and an edge inside a single parcel is never emitted at all, which is what
 * keeps a seam from appearing down the middle of an L-shaped field (D149).
 *
 * The east side of a tile comes back facing 'e' and the south side facing 's', so a
 * painter can tell a boundary that runs away from the eye from one that runs across it.
 */
export function parcelBoundaries(parcels: Iterable<Iterable<TileKey>>): Edge[] {
  const owner = new Map<TileKey, number>();
  let id = 0;
  for (const tiles of parcels) {
    for (const key of tiles) if (parseTileKey(key)) owner.set(key, id);
    id++;
  }
  const edges: Edge[] = [];
  for (const [key, mine] of owner) {
    const p = parseTileKey(key);
    if (!p) continue;
    const [x, y] = p;
    const east = owner.get(`${x + 1},${y}`);
    if (east !== undefined && east !== mine)
      edges.push({ x1: x + 1, y1: y, x2: x + 1, y2: y + 1, facing: 'e' });
    const south = owner.get(`${x},${y + 1}`);
    if (south !== undefined && south !== mine)
      edges.push({ x1: x, y1: y + 1, x2: x + 1, y2: y + 1, facing: 's' });
  }
  return mergeRuns(edges);
}

/** Drops the points in the middle of a straight run, so a rectangle has four corners. */
function simplifyRing(ring: Point[]): Point[] {
  const out: Point[] = [];
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const prev = ring[(i - 1 + n) % n] as Point,
      cur = ring[i] as Point,
      next = ring[(i + 1) % n] as Point;
    const ax = cur[0] - prev[0],
      ay = cur[1] - prev[1],
      bx = next[0] - cur[0],
      by = next[1] - cur[1];
    if (ax * by - ay * bx !== 0) out.push(cur);
  }
  return out.length >= 3 ? out : ring;
}

/**
 * The outline of a tile set as closed rings of world-space points, corners only. A
 * rectangle gives one ring of four points; an L gives one ring of six; a field with a hole
 * in it gives two rings. Projecting these, rather than one polygon per tile, is what D149
 * asks for: no internal seam can show, because no internal edge is ever drawn.
 */
export function unionRings(tiles: Iterable<TileKey>, clipY = Infinity): Point[][] {
  const set = tileSet(tiles);
  const b = unionBounds(set);
  if (!b.w) return [];
  // The rectangle fast path, which is still the common case on a small world.
  if (clipY === Infinity && set.size === b.w * b.h) {
    return [
      [
        [b.x, b.y],
        [b.x + b.w, b.y],
        [b.x + b.w, b.y + b.h],
        [b.x, b.y + b.h],
      ],
    ];
  }
  const edges = unionEdges(set, clipY);
  const from = new Map<string, Edge[]>();
  for (const e of edges) {
    const key = `${e.x1},${e.y1}`;
    const list = from.get(key);
    if (list) list.push(e);
    else from.set(key, [e]);
  }
  const used = new Set<Edge>();
  const rings: Point[][] = [];
  for (const start of edges) {
    if (used.has(start)) continue;
    const ring: Point[] = [];
    let e: Edge | undefined = start;
    while (e && !used.has(e)) {
      used.add(e);
      ring.push([e.x1, e.y1]);
      // Two edges leave the same point only where the shape pinches to a corner; either
      // choice closes a ring, and both rings get walked.
      e = (from.get(`${e.x2},${e.y2}`) ?? []).find((n) => !used.has(n));
    }
    if (ring.length >= 3) rings.push(simplifyRing(ring));
  }
  return rings;
}

/**
 * The union's outline as an SVG path, projected. Several rings become several subpaths,
 * which an even-odd fill reads as a shape with a hole in it.
 */
export function unionPath(tiles: Iterable<TileKey>, z = 0, clipY = Infinity): string {
  return unionRings(tiles, clipY)
    .map((ring) => polyPath(ring.map(([x, y]) => project(x, y, z))))
    .join(' ');
}

/**
 * The x ranges the union covers at a world y: one span for a rectangle, two where an
 * L-shaped field has a notch in that row. A texture pass draws a line per span rather than
 * one line across the bounding box, so nothing is painted off the field.
 */
export function rowSpans(tiles: Iterable<TileKey>, y: number): Array<[number, number]> {
  const set = tileSet(tiles);
  const ty = Math.floor(y);
  const xs: number[] = [];
  for (const key of set) {
    const p = parseTileKey(key);
    if (p && p[1] === ty) xs.push(p[0]);
  }
  xs.sort((a, b) => a - b);
  const spans: Array<[number, number]> = [];
  for (const x of xs) {
    const last = spans[spans.length - 1];
    if (last && last[1] === x) last[1] = x + 1;
    else spans.push([x, x + 1]);
  }
  return spans;
}

/* ---------- the run a machine makes (D164) ---------- */

/** Half a machine's width, in tiles. Keeps a rig on its own ground at the end of a run. */
export const MACHINE_INSET = 0.3;

/** Where a machine working a field stands, and the run it travels. World space. */
export interface MachineRun {
  /** Where it stands: the middle of its own lane on the working line. */
  at: Point;
  /** The two ends of that lane, the run it travels between and back. */
  from: Point;
  to: Point;
  /** The length of the run, in tiles. */
  len: number;
}

/**
 * The run a machine makes down a field at a given progress (D164).
 *
 * The working line is where the worked ground stops, `b.y + b.h * t`, which is the same
 * line `paintFieldJob` clips the worked band at: the machine cannot disagree with the
 * ground behind it, and at nine tenths of a job it is nine tenths of the way down. Across
 * that line it travels the widest run the field covers in that row, which is the line a
 * machine really works and the one `longestRun` in the sim times the job by. The row comes
 * from `rowSpans`, so an L-shaped field gives the run of the row the machine is in rather
 * than the box round the field (D149).
 *
 * D154 puts more than one crew on a field, so the run is cut into `lanes` and each machine
 * takes one: two machines are never on the same point, and neighbouring lanes are walked in
 * opposite directions so they stay apart all the way along rather than only in the middle.
 */
export function machineRun(
  tiles: Iterable<TileKey>,
  t: number,
  lane = 0,
  lanes = 1,
): MachineRun | null {
  const set = tileSet(tiles);
  const b = unionBounds(set);
  if (!b.w) return null;
  const p = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  const y = b.y + b.h * p;
  // The row the working line falls in, held inside the field at either end of the job.
  const row = Math.min(Math.max(y - 1e-6, b.y), b.y + b.h - 1e-6);
  let best: [number, number] | undefined;
  for (const s of rowSpans(set, row)) if (!best || s[1] - s[0] > best[1] - best[0]) best = s;
  const span: [number, number] = best ?? [b.x, b.x + b.w];
  const n = Math.max(1, Math.floor(lanes));
  const i = Math.max(0, Math.min(n - 1, Math.floor(lane)));
  const width = (span[1] - span[0]) / n;
  const inset = Math.min(MACHINE_INSET, width / 3);
  const lo = span[0] + width * i + inset,
    hi = span[0] + width * (i + 1) - inset;
  const ends: [Point, Point] =
    i % 2
      ? [
          [hi, y],
          [lo, y],
        ]
      : [
          [lo, y],
          [hi, y],
        ];
  return { at: [(lo + hi) / 2, y], from: ends[0], to: ends[1], len: hi - lo };
}

/**
 * Points scattered over the union rather than over its bounding box, at the density the
 * handoff's count asks for. The count is taken from the tile total, so an L of five tiles
 * gets five tiles' worth of tufts and none of them land in the notch.
 */
export function scatter(tiles: Iterable<TileKey>, r: () => number, n: number, inset = 0): Point[] {
  const set = tileSet(tiles);
  const b = unionBounds(set);
  const out: Point[] = [];
  if (!b.w || n <= 0) return out;
  let guard = 0;
  while (out.length < n && guard++ < n * 40 + 200) {
    const x = b.x + inset + r() * (b.w - inset * 2);
    const y = b.y + inset + r() * (b.h - inset * 2);
    if (inUnion(set, x, y)) out.push([x, y]);
  }
  return out;
}

/* ---------- the token bridge ----------
   The map is painted with hex maths (mix, tint, shade), but its colours belong to the
   design system, not to this file. These names resolve from the live CSS custom
   properties, so the map re-themes with everything else: the same `--map-*` layer the
   sprite props paint with, plus the field ladder from colors.css. The literals in BASE
   are the fallback for a host that has not loaded the stylesheet. A token added to the
   stylesheet and not added here sits unread. */
const TOKEN_MAP = {
  soil: '--field',
  soilPloughed: '--field-ploughed',
  s1: '--field-1',
  s2: '--field-2',
  s3: '--field-3',
  s4: '--field-4',
  s5: '--field-ready',
  pasture: '--pasture',
  wood: '--wood',
  water: '--water',
  road: '--road',
  waste: '--waste',
  yard: '--yard',
  hedge: '--map-hedge',
  wall: '--map-wall',
  stone: '--map-rock',
  roof: '--map-roof',
  barn: '--map-barn',
  slate: '--map-slate',
  trim: '--map-trim',
  fence: '--map-fence',
  steel: '--map-steel',
  roofStorage: '--map-roof-storage',
  roofLivestock: '--map-roof-livestock',
  roofProcessing: '--map-roof-processing',
  roofInfrastructure: '--map-roof-infrastructure',
  roofSales: '--map-roof-sales',
  roofHome: '--map-roof-home',
  ashcombe: '--ashcombe',
  ferrant: '--ferrant',
  norsk: '--norsk',
  /* D259: the dark theme's ground round the world and its grass. Defined only for the dark
     theme; in the light one they are unset and the season's own colours stand. */
  backdrop: '--map-backdrop',
  grass: '--map-grass',
} as const;

/**
 * The resolved token set. Every name is optional: one the host has not defined falls back
 * to its literal in BASE.
 */
export type TokenName = keyof typeof TOKEN_MAP;
export type MapTokens = Partial<Record<TokenName, string>>;

/** The token names the bridge reads, in order. Exported so a test can count them. */
export const TOKEN_NAMES = Object.keys(TOKEN_MAP) as TokenName[];

/* A resolved custom property can come back in any colour space the author wrote it in:
   the --map-* layer is built from color-mix(in oklab, …), which serialises as oklab(…),
   not rgb(…). Canvas normalises whatever it is to sRGB hex, which is what the painters'
   maths needs. A sentinel catches a value the browser could not parse at all. */
let probeCtx: CanvasRenderingContext2D | null = null;

/**
 * oklab(L a b) to sRGB hex. The `--map-*` layer is built from `color-mix(in oklab, …)`,
 * which is what getComputedStyle hands back, and canvas will not parse it. Without this,
 * seven of twenty-five tokens fall back to literals and dark mode quietly diverges (D148).
 */
export function oklabToHex(L: number, a: number, bb: number): string {
  const l = (L + 0.3963377774 * a + 0.2158037573 * bb) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * bb) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * bb) ** 3;
  const lin = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return rgb2hex(
    lin.map((v) => {
      const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(Math.max(v, 0), 1 / 2.4) - 0.055;
      return Math.max(0, Math.min(1, c)) * 255;
    }),
  );
}

/** Any serialised CSS colour to flat hex, or null if nothing here can read it. */
export function cssToHex(css: string | null | undefined): string | null {
  if (!css) return null;
  if (/^#[0-9a-f]{6}$/i.test(css)) return css;
  const rgb = /rgba?\(([^)]+)\)/.exec(css);
  if (rgb) {
    const p = (rgb[1] as string)
      .split(/[\s,/]+/)
      .filter(Boolean)
      .map((v) => parseFloat(v));
    if (!p.slice(0, 3).some(Number.isNaN)) return rgb2hex(p.slice(0, 3));
  }
  const ok = /oklab\(([^)]+)\)/.exec(css);
  if (ok) {
    const p = (ok[1] as string)
      .split(/[\s,/]+/)
      .filter(Boolean)
      .map((v) => parseFloat(v) / (v.includes('%') ? 100 : 1));
    if (p.length >= 3 && !p.slice(0, 3).some(Number.isNaN))
      return oklabToHex(p[0] as number, p[1] as number, p[2] as number);
  }
  try {
    if (typeof document === 'undefined') return null;
    probeCtx = probeCtx || document.createElement('canvas').getContext('2d');
    if (!probeCtx) return null;
    probeCtx.fillStyle = '#010203';
    probeCtx.fillStyle = css;
    const out: unknown = probeCtx.fillStyle;
    return typeof out === 'string' && /^#[0-9a-f]{6}$/i.test(out) && out !== '#010203' ? out : null;
  } catch {
    return null;
  }
}

/* The probe's sentinels. A `var()` the document cannot resolve does not come back empty:
   the declaration is invalid at computed-value time, and `color` inherits instead, which
   on a device in dark appearance is white. A probe that cannot tell failure from a
   legitimate value is not a probe, so it asks for a colour it could never legitimately be
   given. Two of them make that airtight rather than merely unlikely: a property that
   really is the first sentinel answers with the same colour whichever fallback it is
   offered, while an unresolvable one answers with whichever fallback it was offered. So
   agreement means a real colour and disagreement means nothing was there (D173). */
const SENTINEL_A = '#010203';
const SENTINEL_B = '#fe0d04';

/**
 * Resolve the map's colour tokens against a host element, so a value written as
 * `color-mix(...)` or a nested `var(...)` comes back as flat hex the painters can do
 * maths on. Reading the custom property directly hands back the unresolved expression,
 * so each is pushed through a probe's `color` and read back. Anything the probe cannot
 * resolve is left out, and the literals in BASE stand in for it. Call it again whenever
 * the theme changes.
 *
 * This is the only place in the map that touches the document.
 */
export function resolveMapTokens(host: Element | null | undefined): MapTokens | null {
  if (typeof document === 'undefined' || !host) return null;
  const probe = document.createElement('span');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'position:absolute;width:0;height:0;visibility:hidden;pointer-events:none';
  host.appendChild(probe);
  const out: MapTokens = {};
  /* Null rather than a colour whenever the declaration did not take: a host with no
     support for custom properties drops it on the floor, and the colour left standing
     would be the inherited one this guards against. */
  const ask = (prop: string, fallback: string): string | null => {
    probe.style.color = '';
    probe.style.color = `var(${prop}, ${fallback})`;
    if (!probe.style.color) return null;
    return cssToHex(getComputedStyle(probe).color);
  };
  try {
    for (const key of TOKEN_NAMES) {
      const prop = TOKEN_MAP[key];
      const hex = ask(prop, SENTINEL_A);
      if (!hex) continue;
      if (hex.toLowerCase() !== SENTINEL_A) {
        out[key] = hex;
        continue;
      }
      /* The sentinel came back. Either the property is unreachable, or it genuinely is
         this colour; the second fallback is what separates the two. */
      const again = ask(prop, SENTINEL_B);
      if (again && again.toLowerCase() === SENTINEL_A) out[key] = hex;
    }
  } finally {
    probe.remove();
  }
  return out;
}

/* Brighter than the shipped tokens on purpose: the reference worlds read as illustration,
   not as a muted map, and "colours feel drab" was the first complaint. Nothing here is
   used when the stylesheet has loaded; the tokens win. */
export const BASE = {
  soil: '#8A6238',
  soilPloughed: '#6B4A2A',
  stage: ['#6B4A2A', '#A9D36B', '#7CBB4A', '#54A23B', '#2F8433', '#EAB42B'],
  pasture: '#7CBF52',
  meadow: '#9BD05C',
  wood: '#2F7A3C',
  orchard: '#3F8C42',
  water: '#4E97BE',
  road: '#C0B49C',
  track: '#B3A488',
  waste: '#AEA491',
  yard: '#DCCBA6',
  hedge: '#26602F',
  stone: '#C3B9A5',
  roof: '#9A4A34',
  wall: '#EFE4CC',
} as const;

/** A colour and how strongly it is laid on: `["#E6B48C", 0.16]`. */
export type Wash = [string, number];

export interface Season {
  label: string;
  cropTint: Wash;
  cropMix: Wash;
  wood: string;
  meadow: string;
  ground: Wash;
  haze: string;
  sky: string;
  blossom?: boolean;
  stubble?: boolean;
  frost?: boolean;
}

export interface TimeOfDay {
  label: string;
  dx: number;
  dy: number;
  len: number;
  cast: Wash;
  lit: number;
  dark: number;
  sky: string;
}

export const SEASONS: Record<string, Season> = {
  spring: {
    label: 'Spring',
    cropTint: ['#C9B27A', 0.18],
    cropMix: ['#C8E08A', 0.22],
    wood: '#4A8040',
    meadow: '#A6CC66',
    ground: ['#EAD9A8', 0.1],
    haze: '#E9E2CE',
    sky: '#EDE6D4',
    blossom: true,
  },
  summer: {
    label: 'Summer',
    cropTint: ['#E8C56A', 0.06],
    cropMix: ['#2F6B2E', 0.1],
    wood: '#2E5D36',
    meadow: '#93B95C',
    ground: ['#E0C98E', 0.08],
    haze: '#EFE7D2',
    sky: '#F1E9D6',
  },
  autumn: {
    label: 'Autumn',
    cropTint: ['#D79A2E', 0.3],
    cropMix: ['#C98A1B', 0.18],
    wood: '#A96A28',
    meadow: '#BFA75A',
    ground: ['#D9B478', 0.16],
    haze: '#EDE0C4',
    sky: '#F0E3C8',
    stubble: true,
  },
  winter: {
    label: 'Winter',
    cropTint: ['#B9C3C0', 0.34],
    cropMix: ['#6F8A72', 0.22],
    wood: '#35503A',
    meadow: '#A9B296',
    ground: ['#D8D8CE', 0.22],
    haze: '#E6E7E2',
    sky: '#E9EAE5',
    frost: true,
  },
};

export const TIMES: Record<string, TimeOfDay> = {
  /**
   * The sixth band, and the only one that is not the handoff's. Its table was built to
   * show a world off and stops at dusk; this world ticks hourly and a player checks in at
   * two in the morning, so night is derived here (D148). Its direction, length, lit and
   * dark carry on the path dusk is already walking — the light lower and further round,
   * the shadows longer, the modelling softer — and its cast is D144's own night wash,
   * `--map-sky-night` at 0.28, the wash this game has already shipped on the flat map.
   * The token rather than a hex, as the weather casts are, so night re-themes with it.
   */
  night: {
    label: 'Night',
    dx: -1,
    dy: 0.3,
    len: 2.4,
    cast: ['var(--map-sky-night)', 0.28],
    lit: 0.1,
    dark: 0.44,
    sky: '#3A4160',
  },
  dawn: {
    label: 'Dawn',
    dx: 1,
    dy: 0.5,
    len: 1.9,
    cast: ['#E6B48C', 0.16],
    lit: 0.22,
    dark: 0.32,
    sky: '#EEDCC6',
  },
  morning: {
    label: 'Morning',
    dx: 1,
    dy: 0.55,
    len: 1.5,
    cast: ['#F2C98A', 0.12],
    lit: 0.26,
    dark: 0.3,
    sky: '#F3E3C4',
  },
  midday: {
    label: 'Midday',
    dx: 0.35,
    dy: 1,
    len: 0.7,
    cast: ['#FFF6DC', 0.05],
    lit: 0.3,
    dark: 0.22,
    sky: '#F2EBDA',
  },
  evening: {
    label: 'Evening',
    dx: -1,
    dy: 0.5,
    len: 1.8,
    cast: ['#E08A4F', 0.16],
    lit: 0.2,
    dark: 0.34,
    sky: '#EFD2AE',
  },
  dusk: {
    label: 'Dusk',
    dx: -1,
    dy: 0.4,
    len: 2.1,
    cast: ['#6E6A9A', 0.26],
    lit: 0.14,
    dark: 0.4,
    sky: '#9FA0B4',
  },
};

/** The four season ids, as `packages/data/content/seasons.json` spells them. */
export type SeasonKey = 'spring' | 'summer' | 'autumn' | 'winter';
export type TimeKey = 'night' | 'dawn' | 'morning' | 'midday' | 'evening' | 'dusk';

/* ---------- weather ----------
   D144 put a weather tint on the map; the handoff has a time-of-day cast and no weather
   axis at all. The two compose: the cast goes down first and the weather over it, and the
   pair is held to CAST_CAP so a storm at dusk still leaves a board a player can read.
   These are D144's own values and tokens, the same ones `weatherWash` in TileGrid.tsx
   carries; D148 retires that renderer for the Farm screen, at which point this is their
   only home. Nothing here is wired to a screen yet. */

/** A layer laid over the whole scene: a token or a hex, and an opacity. */
export interface Cast {
  fill: string;
  opacity: number;
}

/** The most the time of day and the weather together may darken the board (D144). */
export const CAST_CAP = 0.38;

export const WEATHER_CAST: Record<Weather, Cast> = {
  // Fair is the weather the palette was chosen under, so it wears no tint.
  fair: { fill: 'transparent', opacity: 0 },
  dry: { fill: 'var(--map-wx-dry)', opacity: 0.1 },
  rain: { fill: 'var(--map-wx-rain)', opacity: 0.16 },
  storm: { fill: 'var(--map-wx-storm)', opacity: 0.24 },
  frost: { fill: 'var(--map-wx-frost)', opacity: 0.14 },
};

/**
 * The layers over the finished scene, back to front: the time of day's cast, then the
 * weather. A caller paints each as a full-bleed rect with `mix-blend-mode: multiply`.
 * A layer at no opacity is left out, so fair weather costs nothing.
 */
export function castLayers(timeKey: TimeKey | string, weather?: Weather): Cast[] {
  const T = TIMES[timeKey] ?? (TIMES.morning as TimeOfDay);
  const out: Cast[] = [{ fill: T.cast[0], opacity: T.cast[1] }];
  const wx = (weather && WEATHER_CAST[weather]) || WEATHER_CAST.fair;
  const opacity = Math.min(wx.opacity, Math.max(0, CAST_CAP - T.cast[1]));
  if (opacity > 0) out.push({ fill: wx.fill, opacity });
  return out;
}

/* ---------- the soil overlay ----------
   D60's soil overlay is a multiply layer over each field, at the strength the flat map
   uses: a field at 100 wears nothing, a field at 0 wears the soil colour at full. The
   default is the one TileGrid carries, so a field with no reading is drawn as middling
   rather than as ruined. */

/** The soil reading a field with no entry is drawn at. */
export const SOIL_DEFAULT = 70;

/** How strongly the soil wash is laid on a field, from its 0 to 100 reading. */
export function soilOpacity(value?: number): number {
  const v = value === undefined || !Number.isFinite(value) ? SOIL_DEFAULT : value;
  return Math.max(0, Math.min(1, 1 - v / 100));
}

/**
 * The handoff's crop keys are the short ones the screens use; `packages/data` spells two
 * of them out. Same crop, same hue: this is a spelling, not a number.
 */
const CROP_ALIAS: Record<string, string> = { sugar_beet: 'beet', oilseed_rape: 'oilseed' };

/** A crop id as the hue, label and plant tables spell it. */
export const cropKey = (crop?: string): string => (crop ? (CROP_ALIAS[crop] ?? crop) : '');

/**
 * D196: what a building is for, spelled as `packages/data` spells a building's `group`.
 * The map tints the roof by it and paints its sign from it.
 */
export type BuildingUse =
  | 'storage'
  | 'livestock'
  | 'processing'
  | 'infrastructure'
  | 'sales'
  /** D199: the farmhouse, the one building of its kind. */
  | 'home';

/** The working palette a painter is handed. */
export interface Palette {
  S: Season;
  T: TimeOfDay;
  /** D440: how far winter's snow lies, 0 to 1 (`snowed`); none when left out. */
  snow?: number;
  crop: string[];
  soil: string;
  pasture: string;
  meadow: string;
  wood: string;
  orchard: string;
  water: string;
  road: string;
  track: string;
  waste: string;
  yard: string;
  hedge: string;
  stone: string;
  roof: string;
  wall: string;
  barn: string;
  slate: string;
  trim: string;
  fence: string;
  steel: string;
  /** D196: the roof a building wears for what it is used for. */
  roofs: Record<BuildingUse, string>;
  maker: Record<Maker, string>;
  haze: string;
  sky: string;
  lit: number;
  dark: number;
  cast: Wash;
  /** The cast and the weather, ready to lay over the finished scene. */
  casts: Cast[];
  far: (c: string) => string;
}

/**
 * Working palette for a season and time of day. `tk` is the resolved token set from
 * resolveMapTokens; without it the literals in BASE stand in, so the painters still work
 * in a host with no stylesheet. `weather` reaches only `casts`: the ground keeps the
 * season's colour and the weather is a layer over the whole board (D144).
 */
export function palette(
  seasonKey: SeasonKey | string,
  timeKey: TimeKey | string,
  tk?: MapTokens | null,
  weather?: Weather,
): Palette {
  const S = SEASONS[seasonKey] ?? (SEASONS.summer as Season),
    T = TIMES[timeKey] ?? (TIMES.morning as TimeOfDay);
  const t: MapTokens = tk || {};
  const ladder = [
    t.soilPloughed || BASE.soilPloughed,
    t.s1 || (BASE.stage[1] as string),
    t.s2 || (BASE.stage[2] as string),
    t.s3 || (BASE.stage[3] as string),
    t.s4 || (BASE.stage[4] as string),
    t.s5 || (BASE.stage[5] as string),
  ];
  const green = (c: string) => vivid(c, RICHER.greens);
  const roof = (c: string) => vivid(c, RICHER.roofs);
  const crop = ladder.map((c, i) => {
    if (i === 0) return mix(c, S.ground[0], S.ground[1] * 0.5);
    if (i === 5) return mix(c, S.cropTint[0], S.cropTint[1] * 0.4);
    return green(mix(mix(c, S.cropMix[0], S.cropMix[1]), S.cropTint[0], S.cropTint[1] * 0.5));
  });
  const soilBase = t.soil || BASE.soil;
  const pastureBase = t.pasture || BASE.pasture;
  const woodBase = t.wood || BASE.wood;
  return {
    S,
    T,
    crop,
    soil: mix(soilBase, S.ground[0], S.ground[1]),
    // D381: the greens and the roofs a little richer (`RICHER`), in both renderers alike.
    pasture: green(mix(pastureBase, t.grass ? mix(t.grass, S.meadow, 0.2) : S.meadow, 0.45)),
    meadow: green(t.grass ? mix(t.grass, S.meadow, 0.2) : S.meadow),
    wood: green(mix(woodBase, S.wood, 0.72)),
    orchard: green(mix(t.wood || BASE.orchard, S.wood, 0.5)),
    water: mix(t.water || BASE.water, S.haze, seasonKey === 'winter' ? 0.22 : 0.05),
    road: mix(t.road || BASE.road, S.ground[0], 0.12),
    track: mix(BASE.track, S.ground[0], 0.14),
    waste: mix(t.waste || BASE.waste, S.ground[0], 0.12),
    yard: mix(t.yard || BASE.yard, S.ground[0], 0.1),
    hedge: green(mix(t.hedge || BASE.hedge, S.wood, 0.35)),
    stone: t.stone || BASE.stone,
    roof: roof(t.roof || BASE.roof),
    wall: t.wall || BASE.wall,
    /* Building and machine materials, straight from the token layer. */
    barn: roof(t.barn || '#B8352B'),
    slate: t.slate || '#5A6066',
    trim: t.trim || '#F2EDE1',
    fence: t.fence || '#A8875C',
    steel: t.steel || '#6B6B66',
    /* D196: a roof by use, so a store, a shed for stock and a works read apart at any zoom.
       The fallbacks are the stylesheet's own mixes at rest in the light theme. */
    roofs: {
      storage: roof(t.roofStorage || '#8A7442'),
      livestock: roof(t.roofLivestock || '#8E4A3C'),
      processing: roof(t.roofProcessing || '#50707F'),
      infrastructure: roof(t.roofInfrastructure || t.slate || '#5A6066'),
      sales: roof(t.roofSales || '#4A6A4C'),
      home: roof(t.roofHome || '#A2503A'),
    },
    /* The three makers, the only badge of ownership the map carries. */
    maker: {
      ashcombe: t.ashcombe || '#3E7A46',
      ferrant: t.ferrant || '#B8352B',
      norsk: t.norsk || '#2E5F9E',
    },
    haze: S.haze,
    sky: t.backdrop ? mix(t.backdrop, T.sky, 0.08) : mix(S.sky, T.sky, 0.45),
    lit: T.lit,
    dark: T.dark,
    cast: T.cast,
    casts: castLayers(timeKey, weather),
    /* D259: on the dark theme the country is hazed into the dark ground, not the pale one. */
    far: (c: string) =>
      t.backdrop ? mix(c, t.backdrop, 0.62) : mix(mix(c, '#8E9A84', 0.26), S.haze, 0.38),
  };
}

/* ---------- parcels ---------- */

export type ParcelKind =
  'field' | 'pasture' | 'meadow' | 'wood' | 'orchard' | 'pond' | 'waste' | 'yard';

/** 0 ploughed, 5 ready. */
export type Stage = 0 | 1 | 2 | 3 | 4 | 5;

/* ---------- D353: a worked field's own cycle, on the render clock ----------
   A field run by a hand passes through its stages as a whole (growing, ripe, harvested by its
   workers, regrowing) on a cycle of its own, `FIELD_CYCLE` in the appearance data, replacing
   D341's rolling wave. Pure functions of a clock and the field's own id: the parcel carries only
   `cycle`, which never changes while the hand is on it, so a store change, a tap or a rebuild
   never steps a field. No clock (reduced motion) holds every field at the cycle's `still`. */

/** A field cycle's shape: its length and where each part of it ends, as `FIELD_CYCLE`. */
export interface FieldCycle {
  readonly ms: number;
  readonly grow: number;
  readonly ripe: number;
  readonly harvest: number;
  readonly still: number;
}

/** A field's own offset into the cycle, 0 to 1, from its id, so fields do not step together. */
function cycleOffset(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967296;
}

/** Where a field is in its cycle at `t` (ms on the render clock), 0 to 1; `still` with no clock. */
export function fieldCyclePhase(t: number | undefined, id: string, c: FieldCycle): number {
  if (t === undefined || !Number.isFinite(t)) return c.still;
  const p = t / c.ms + cycleOffset(id);
  return ((p % 1) + 1) % 1;
}

/** The stage the whole field shows at `phase`: growing 1 to 4, ripe and harvested 5, cut 0. */
export function fieldCycleStage(phase: number, c: FieldCycle): Stage {
  if (phase < c.grow) return Math.min(4, 1 + Math.floor((phase / c.grow) * 4)) as Stage;
  if (phase < c.harvest) return 5;
  return 0;
}

/** Whether the field's workers are harvesting it at `phase` (chaff over it). */
export function fieldCycleHarvesting(phase: number, c: FieldCycle): boolean {
  return phase >= c.ripe && phase < c.harvest;
}

/** A worked field as it looks at `t`: its stage, and `harvest` while it is being cut. Any
 *  other parcel is returned as it is. */
export function atCycle(p: IsoParcel, t: number | undefined, c: FieldCycle): IsoParcel {
  if (!p.cycle) return p;
  const phase = fieldCyclePhase(t, p.id ?? p.tiles[0] ?? '', c);
  const out: IsoParcel = { ...p, stage: fieldCycleStage(phase, c) };
  if (fieldCycleHarvesting(phase, c)) out.harvest = true;
  else delete out.harvest;
  return out;
}

/** D345: the flat map's own lift for a crop's "ready" icon above the ground, in `project`'s
 *  own pre-scale units (as `paintTierSign`'s own lift is), and how wide a tap near it still
 *  finds it, in screen pixels so it is just as easy to tap whatever the zoom. One pair, so a
 *  tap can never disagree with where the icon is actually drawn. The 3D map keeps its own. */
export const READY_LIFT = 22,
  READY_TAP_R = 28;

/** `hen` is the idle world's (D240): small, white, with a red comb. D284: one list for both renderers. */
export type AnimalKind = LookAnimal;

/**
 * The job the map can draw. `packages/data`'s `Job` enum is the vocabulary a machine is
 * listed under and the one the handoff's rig table is keyed by; `JobKind` in
 * `packages/sim` is the narrower one a field job carries. The map takes either and draws
 * no machine at all for a kind it has no rig for, which is what the handoff does.
 */
export type IsoJobKind = string;

export interface IsoJob {
  kind: IsoJobKind;
  progress: number;
  maker?: Maker;
  /**
   * D154: a field can be worked by more than one crew, and each crew brings a machine. One
   * machine is drawn per entry, each in its own lane. Absent, the field shows the one
   * machine `maker` names.
   */
  makers?: Maker[];
}

/**
 * A parcel is a set of tiles, not a rectangle (D149). A 2x2 wheat field and an L of five
 * tiles are both one parcel, painted as one polygon with one texture pass.
 */
export interface IsoParcel {
  tiles: TileKey[];
  t: ParcelKind;
  stage?: Stage;
  crop?: string;
  animal?: AnimalKind;
  n?: number;
  job?: IsoJob;
  /** D239: the tiles of a water parcel that carry a bridge. */
  bridges?: TileKey[];
  /** The idle world's bees (D240): a meadow with this many hives in place of its bales. */
  hives?: number;
  /** The idle world's vines (D240): an orchard planted as rows of vines, not trees. */
  vines?: boolean;
  /** D251: the idle world's hops, an orchard planted as poles with bines climbing them. */
  hops?: boolean;
  /** D300: the idle world's tomatoes, an orchard plot of glasshouses, not trees. */
  glasshouse?: boolean;
  /** D251: the idle world's saffron, a meadow laid out as low beds of purple crocus. */
  crocus?: boolean;
  /** D251: a pasture trodden to mud, the pigs' pen. */
  mud?: boolean;
  /** D268: how many of the producer's milestones are reached. Crops grow denser and richer,
   * animals take better breeds, orchard trees grow fuller. */
  tier?: number;
  /** D268: the tier sign's words, the last milestone reached: `25`. None below the first. */
  sign?: string;
  /** D268: a hand works this plot, so a small figure stands on it. */
  hand?: boolean;
  /** D331: the worker decoration the hands on it wear. */
  workers?: string;
  /** D268: a milestone has just been reached here, so the plot plays its short moment. */
  burst?: boolean;
  /** D353: a hand works this field, so it runs its own cycle on the render clock (`atCycle`)
   *  rather than showing its bar. Never changes while the hand is on it. */
  cycle?: boolean;
  /** D353: set by `atCycle` only, while the field's workers are harvesting it. */
  harvest?: boolean;
  /** D345: no hand, and the bar is full: a crop waiting to be tapped, with a small icon
   *  bobbing over the field. */
  ready?: boolean;
  /** D454: the bar is full, so the marker collects; false when the marker marks a buy, a hire or a build. */
  due?: boolean;
  /** D245: a harvest festival's lasting mark, standing on a meadow tile in place of bales. */
  mark?: string;
  /** D259: the mark has just gone down, so it is drawn with the arrival pulse. */
  markNew?: boolean;
  /** D327: a monument ground: which ground on the world it is (0 first), and the marks it holds. */
  ground?: { n: number; marks: string[] };
  /** D291: the fence decoration round a pasture. */
  fence?: string;
  /** D291: the path decoration the yard is laid with. */
  path?: string;
  /** D340: the producer this parcel grows or houses, if any, so a tap finds the world list's
   *  row for it. Bare land (meadow waiting for the next purchase) and yard carry none. */
  id?: string;
  /** D422: how many units of its producer are owned, for a kit whose plots grow by count. */
  owned?: number;
}

/**
 * D424: a producer's level for the asset module, from its own milestones in the data
 * (`milestones[].owned`): 0 its first unit, 1 growing, 2 full size from five, then one more for
 * every milestone reached. Pass it with the owned count: `A.<theme>[i](level, units)`.
 */
export function assetLevel(units: number, milestones: readonly number[]): number {
  if (units < 5) return units <= 1 ? 0 : 1;
  return 2 + milestones.filter((m) => units >= m).length;
}

/**
 * D424: how much taller a hero stands at a level, a step a milestone up to the module's top
 * (1.56), as the module's `staged` (game edit) scales it; for a flat painter to match.
 */
export function levelHeight(level: number, steps = 16): number {
  return level <= 2 ? 1 : 1 + 0.56 * Math.min(1, (level - 2) / steps);
}

/**
 * D422, D423: the heart, the theme's home, standing on the world's yard in place of the home and
 * the store. `yard` is the seeded yard; `dir` the way the store grows from the home (+1 east, -1
 * west when the world is mirrored, as the barn's `barnDir`); `producers` how many producers are
 * running, `years` the festivals held and `generations` how often the world has been passed on
 * (the three things the heart grows by); `store` the storage upgrades bought (barn level less 1).
 */
export interface Heart {
  yard: Bounds;
  dir: 1 | -1;
  producers: number;
  years: number;
  generations: number;
  store: number;
}

/**
 * D430: the world's age, years (festivals held) and generations (times passed on), uncapped,
 * from its home: the heart's own drivers, or the home's `age` on a theme with no heart.
 */
export function worldAge(
  buildings:
    ReadonlyArray<{ heart?: Heart; age?: { years: number; generations: number } }> | undefined,
): { years: number; generations: number } {
  for (const b of buildings ?? []) {
    if (b.heart) return { years: b.heart.years, generations: b.heart.generations };
    if (b.age) return b.age;
  }
  return { years: 0, generations: 0 };
}

/**
 * D430: how far the module's country has grown, as its `scenery` reckons it: the world's own
 * progress, plus up to 0.2 from years and 0.2 from generations, to 1.4 at most. For a flat painter
 * that places what the module places; the module itself is given the raw progress and the age.
 */
export function fameProgress(
  progress: number,
  age: { years: number; generations: number },
): number {
  return Math.min(
    1.4,
    Math.max(progress, 0) + Math.min(0.2, age.years * 0.02) + Math.min(0.2, age.generations * 0.07),
  );
}

/**
 * D423: where the heart and its store stand on the yard. The heart is square and kept whole,
 * scaled evenly, never stretched: the yard's full depth, at the home's end, front aligned. With
 * no storage upgrades it takes the most of the yard it can (6 of a 6 by 6 yard); with some, one
 * tile at least is kept on the store's side, and the store fills that side in bays, one a level,
 * along its longer side then in rows. `size` is the heart's side in tiles, `bays` each bay's
 * footprint, nearest the heart first.
 */
export function heartPlan(h: Heart): { x: number; y: number; size: number; bays: Bounds[] } {
  const { yard, dir } = h;
  const want = Math.max(0, h.store);
  const size = want ? Math.min(yard.h, yard.w - 1) : Math.min(yard.w, yard.h);
  const x = dir > 0 ? yard.x : yard.x + yard.w - size;
  const y = yard.y + yard.h - size;
  const bays: Bounds[] = [];
  const rw = yard.w - size;
  if (want && rw > 0) {
    const rx = dir > 0 ? yard.x + size : yard.x;
    const along = rw >= yard.h; // the store's region runs along x when it is wider than deep
    const long = along ? rw : yard.h;
    const short = along ? yard.h : rw;
    const depth = 1.4;
    const rows = Math.max(1, Math.floor(short / depth));
    const cols = Math.floor(long);
    const n = Math.min(want, rows * cols);
    for (let i = 0; i < n; i++) {
      const c = i % cols,
        r = Math.floor(i / cols);
      // Nearest the heart first, from the yard's front.
      const a = dir > 0 || !along ? c : cols - 1 - c;
      const b = Math.min(depth, short / rows);
      if (along)
        bays.push({
          x: rx + a + 0.08,
          y: yard.y + yard.h - (r + 1) * b + 0.08,
          w: 0.84,
          h: b - 0.16,
        });
      else {
        const bx = dir > 0 ? rx + r * b : rx + rw - (r + 1) * b;
        bays.push({ x: bx + 0.08, y: yard.y + yard.h - (a + 1) + 0.08, w: b - 0.16, h: 0.84 });
      }
    }
  }
  return { x, y, size, bays };
}

/**
 * D437: the heart's form, the one clock every layout move keeps to, as the asset module's
 * `heart()` reckons it: form 1 at 3 years or 7 producers running, form 2 at 7 years or 15.
 */
export function heartForm(producers: number, years: number): 0 | 1 | 2 {
  const p = producers >= 15 ? 2 : producers >= 7 ? 1 : 0;
  const y = years >= 7 ? 2 : years >= 3 ? 1 : 0;
  return Math.max(p, y) as 0 | 1 | 2;
}

/**
 * D437: the one-tile gaps between neighbouring plots, which close into the theme's paving from
 * form 1: each tile outside every plot with one plot on one side and another plot on the
 * opposite side, in a row or a column, that is not in `keep` (the river, the pond, the yard and
 * anything already standing there). Tile keys, in grid order.
 */
export function plotGaps(plots: readonly Bounds[], keep: ReadonlySet<TileKey>): TileKey[] {
  const owner = new Map<TileKey, number>();
  plots.forEach((b, i) => {
    for (let y = b.y; y < b.y + b.h; y++)
      for (let x = b.x; x < b.x + b.w; x++) owner.set(`${x},${y}`, i);
  });
  const out = new Set<TileKey>();
  const at = (x: number, y: number) => owner.get(`${x},${y}`);
  for (const b of plots)
    for (let y = b.y - 1; y <= b.y + b.h; y++)
      for (let x = b.x - 1; x <= b.x + b.w; x++) {
        const k = `${x},${y}`;
        if (owner.has(k) || keep.has(k)) continue;
        const l = at(x - 1, y),
          r = at(x + 1, y),
          u = at(x, y - 1),
          d = at(x, y + 1);
        if (
          (l !== undefined && r !== undefined && l !== r) ||
          (u !== undefined && d !== undefined && u !== d)
        )
          out.add(k);
      }
  return [...out].sort((a, b) => {
    const [ax, ay] = a.split(',').map(Number) as [number, number];
    const [bx, by] = b.split(',').map(Number) as [number, number];
    return ay - by || ax - bx;
  });
}

/** D437: one straight run of lane, from corner (`x`, `y`) `len` tiles along x or along y. */
export interface LaneRun {
  x: number;
  y: number;
  along: 'x' | 'y';
  len: number;
}

/**
 * D437: the open track (D287) as straight runs of lane, each step taken once: the dry steps
 * along each grid line joined end to end, broken where the track crosses water on a bridge.
 */
export function laneRuns(
  edges: ReadonlyArray<{
    from: { x: number; y: number };
    to: { x: number; y: number };
    wet: boolean;
  }>,
): LaneRun[] {
  const lines = new Map<string, Array<[number, number]>>();
  for (const e of edges) {
    if (e.wet) continue;
    const along = e.from.y === e.to.y ? 'x' : 'y';
    const at = along === 'x' ? e.from.y : e.from.x;
    const a = along === 'x' ? Math.min(e.from.x, e.to.x) : Math.min(e.from.y, e.to.y);
    const b = along === 'x' ? Math.max(e.from.x, e.to.x) : Math.max(e.from.y, e.to.y);
    const key = `${along}${at}`;
    let l = lines.get(key);
    if (!l) lines.set(key, (l = []));
    l.push([a, b]);
  }
  const out: LaneRun[] = [];
  for (const [key, list] of lines) {
    const along = key[0] as 'x' | 'y';
    const at = Number(key.slice(1));
    list.sort((p, q) => p[0] - q[0]);
    let [a, b] = list[0]!;
    const push = () =>
      out.push(
        along === 'x' ? { x: a, y: at, along, len: b - a } : { x: at, y: a, along, len: b - a },
      );
    for (const [c, d] of list.slice(1)) {
      if (c <= b) b = Math.max(b, d);
      else {
        push();
        [a, b] = [c, d];
      }
    }
    push();
  }
  return out.sort((p, q) => p.y - q.y || p.x - q.x || (p.along < q.along ? -1 : 1));
}

/** D437: how many lanes carry traffic at each form of the heart: none, then three, then six. */
export const LANE_TRAFFIC = [0, 3, 6] as const;

/** D437: the runs that carry traffic at `form`: the longest, two tiles at least, longest first. */
export function laneTraffic(runs: readonly LaneRun[], form: 0 | 1 | 2): LaneRun[] {
  return runs
    .filter((r) => r.len >= 2)
    .sort((p, q) => q.len - p.len || p.y - q.y || p.x - q.x)
    .slice(0, LANE_TRAFFIC[form]);
}

/**
 * D440: the share of winter over which the snow comes in, from none at winter's first day to
 * full cover; it lies until the year ends.
 */
export const SNOW_FADE = 0.2;

/** D440: how far the snow has come in `season` at `progress` through it, 0 to 1. */
export function snowAmount(season: string, progress: number): number {
  if (season !== 'winter') return 0;
  // In steps of a twentieth, so a map is redrawn for it now and then, not on every tick.
  return Math.round(Math.max(0, Math.min(1, progress / SNOW_FADE)) * 20) / 20;
}

/** D440: how far the snow covers grass and the country, and rests the fields, at full snow. */
export const WINTER_COVER = { grass: 0.82, field: 0.7, soil: 0.55 } as const;

/**
 * D440: winter over the whole map, both renderers: the grass, the yard's ground and the soil
 * under snow by `snow` (0 to 1), and every crop stage resting under it, so no field grows.
 */
export function snowed(P: Palette, snow: number): Palette {
  if (!(snow > 0)) return P;
  const s = (c: string, k: number) => mix(c, SNOW.colour, k * snow);
  const rest = s(P.soil, WINTER_COVER.field);
  return {
    ...P,
    snow,
    pasture: s(P.pasture, WINTER_COVER.grass),
    meadow: s(P.meadow, WINTER_COVER.grass),
    orchard: s(P.orchard, WINTER_COVER.grass),
    soil: s(P.soil, WINTER_COVER.soil),
    crop: P.crop.map((c) => mix(c, rest, snow)),
  };
}

/** Fill for one parcel, crop hue folded in so each crop reads differently. */
export function parcelFill(p: IsoParcel, P: Palette): string {
  switch (p.t) {
    case 'field': {
      const stage = p.stage ?? 0;
      /* D301: watercress is a green bed in shallow running water, never the cereal ladder
         that would ripen it gold like a dry field. */
      if (cropKey(p.crop) === 'watercress') return mix(P.water, '#2E6B4A', 0.55 + stage * 0.03);
      /* D269: a root crop's ground is its ridged soil at every stage, with only its own leaf
         colour coming through. It never takes the cereal ladder, so it is never green wheat
         and never golden. */
      const root = ROOT_LEAF[cropKey(p.crop)];
      if (root) {
        const ground = mix(P.soil, P.crop[0] as string, 0.4);
        return stage === 0 ? (P.crop[0] as string) : mix(ground, root, 0.12 + stage * 0.05);
      }
      const base = P.crop[stage] as string;
      const hue = CROP_HUE[cropKey(p.crop)];
      if (!hue || stage === 0) return base;
      const rich = Math.min(4, p.tier ?? 0) * 0.04;
      return mix(base, hue[0], hue[1] * (stage === 5 ? 0.12 : 0.35 + stage * 0.14) + rich);
    }
    case 'pasture':
      // D440: in winter the animals are indoors and their pens lie under snow, as in 3D.
      if (P.S.frost) return mix(P.pasture, SNOW.colour, SNOW.roof);
      return p.mud ? mix(P.pasture, P.soil, 0.62) : P.pasture;
    case 'meadow':
      return p.crocus ? mix(P.meadow, P.soil, 0.5) : P.meadow;
    case 'wood':
      return P.wood;
    case 'orchard':
      // D300: a glasshouse plot's ground is in shadow under the glass, not open orchard turf.
      return p.glasshouse ? mix(P.orchard, P.soil, 0.3) : P.orchard;
    case 'pond':
      // D301: a trout pond reads darker than a field's puddle, so it never looks like a flat
      // blue tile.
      return shade(P.water, 0.18);
    case 'waste':
      return P.waste;
    case 'yard':
      // D291: a path decoration lays the yard with cobbles, brick or flagstones.
      return (p.path && PATH_DECOR[p.path]) || P.yard;
    default:
      return P.soil;
  }
}

const CROP_LABEL: Record<string, string> = {
  wheat: 'Wheat',
  barley: 'Barley',
  oats: 'Oats',
  carrots: 'Carrots',
  potatoes: 'Potatoes',
  cabbage: 'Cabbage',
  beet: 'Sugar beet',
  maize: 'Maize',
  oilseed: 'Oilseed rape',
  rapeseed: 'Rapeseed',
  hay: 'Hay',
  strawberries: 'Strawberries',
  watercress: 'Watercress',
};

const LAND_LABEL: Record<string, string> = {
  orchard: 'Orchard',
  wood: 'Woodland',
  meadow: 'Meadow',
  pond: 'Pond',
  waste: 'Rough ground',
  yard: 'The yard',
};

/** Sentence case, no "L2" — see docs/STYLE.md. */
export function parcelLabel(p?: IsoParcel | null): string {
  if (!p) return '';
  if (p.t === 'field')
    return (p.stage ?? 0) === 0
      ? 'Ploughed'
      : `${CROP_LABEL[cropKey(p.crop)] || p.crop || ''}${p.stage === 5 ? ', ready' : ''}`;
  if (p.t === 'pasture') return p.animal === 'cow' ? `${p.n} cows` : `${p.n} sheep`;
  if (p.t === 'orchard' && p.glasshouse) return 'Glasshouse';
  return LAND_LABEL[p.t] || p.t;
}

const LAND_ICON: Record<string, string> = {
  wood: 'woodland',
  orchard: 'apples',
  meadow: 'wildflower_strip',
  pond: 'water',
  waste: 'stone',
  yard: 'grain_silo',
};

/**
 * The icon for a parcel. Derived from what is growing or grazing, not just the land type,
 * and kept beside parcelLabel so the two can never disagree. Crop keys the screens use
 * (beet, oilseed) resolve through Icon's ALIAS map to the art set's own ids.
 */
export function parcelIcon(p?: IsoParcel | null): string {
  if (!p) return 'pasture';
  if (p.t === 'field') return (p.stage ?? 0) === 0 ? 'plough' : p.crop || 'seed';
  if (p.t === 'pasture') return p.animal === 'cow' ? 'cows' : 'sheep';
  return LAND_ICON[p.t] || 'pasture';
}

/** Capital-initial so consumers can reach these off the bundle namespace. */
export const Parcel = { label: parcelLabel, icon: parcelIcon, fill: parcelFill };

/**
 * D435: where the compass sits on either map, at its bottom left: 12px in, and clear of the
 * world tab's sheet, which rides 22px up over the map, as the map's other button is.
 */
export const COMPASS_AT = { left: 12, bottom: 34 } as const;
